import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Input, message } from 'antd';
import React, { useState } from 'react';
import { CdArtifactRecord, CdReleaseApproval, CdReleasePlan, cdDemoApi } from './cdDemoApi';

interface ReleaseApprovalGateProps {
  plan: CdReleasePlan;
  artifact?: CdArtifactRecord;
  approvals?: CdReleaseApproval[];
  onChanged: () => Promise<void> | void;
}

export const ReleaseApprovalGate: React.FC<ReleaseApprovalGateProps> = ({ plan, artifact, approvals = [], onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('Approved after reviewing the exact artifact, Quality evidence, target, and data policy.');
  const gate = artifact?.approvalGate;
  const qualityReady = artifact?.testGate?.status === 'passed';

  const request = async () => {
    if (!artifact) return;
    setBusy(true);
    try {
      await cdDemoApi.requestApproval(plan.id, artifact.id);
      message.success('Release approval requested from the preset Release Manager persona.');
      await onChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Approval could not be requested.');
    } finally { setBusy(false); }
  };

  const decide = async (decision: 'approved' | 'rejected') => {
    if (!gate?.approvalId) return;
    setBusy(true);
    try {
      const decisionNote = decision === 'approved'
        ? note
        : 'Rejected for demo: the release window requires additional business confirmation.';
      await cdDemoApi.decideApproval(gate.approvalId, decision, decisionNote);
      message.success(decision === 'approved' ? 'Exact release scope approved.' : 'Release request rejected; Production remains unchanged.');
      await onChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Approval decision could not be saved.');
    } finally { setBusy(false); }
  };

  const icon = gate?.status === 'approved' ? <CheckCircleFilled />
    : gate?.status === 'rejected' ? <CloseCircleFilled />
      : gate?.status === 'pending' ? <ClockCircleFilled /> : <SafetyCertificateOutlined />;

  return (
    <section className={`release-approval-gate is-${gate?.status ?? 'missing'}`} aria-label="Release approval gate">
      <div className="approval-gate-heading">
        {icon}
        <div>
          <span>RELEASE APPROVAL</span>
          <h3>{gate?.status === 'approved' ? 'Approved for this exact Production scope'
            : gate?.status === 'pending' ? 'Waiting for Release Manager decision'
              : gate?.status === 'rejected' ? 'Release request rejected'
                : gate?.status === 'stale' ? 'Previous approval is stale' : 'Business approval required'}</h3>
          <p>{!artifact ? 'A sealed artifact is required before approval can be requested.'
            : !qualityReady ? 'Quality Center must sign this exact checksum before business approval begins.'
              : gate?.message ?? 'Request a decision bound to this artifact, target, policy, and Quality report.'}</p>
        </div>
        <b>{gate?.status ?? 'missing'}</b>
      </div>

      {artifact && <div className="approval-scope">
        <div><span>Artifact</span><strong>{artifact.version} · <code>{artifact.sha256.slice(0, 12)}</code></strong></div>
        <div><span>Target</span><strong>{plan.environment} · {plan.targetAppName}</strong></div>
        <div><span>Quality evidence</span><strong>{artifact.testGate?.runId ?? 'Required'}</strong></div>
        <div><span>Data policy</span><strong>{plan.dataPolicy === 'structure-only' ? 'Use sealed QVF data' : 'Reload target connections'}</strong></div>
      </div>}

      {gate?.status === 'approved' && <div className="approval-evidence">
        <TeamOutlined /><span><strong>{gate.decidedBy}</strong><small>{gate.approvalId} · {gate.decidedAt ? new Date(gate.decidedAt).toLocaleString() : 'Decision recorded'}</small></span><p>{gate.decisionNote}</p>
      </div>}

      {gate?.status === 'rejected' && gate.decisionNote && <div className="approval-rejection"><strong>Decision note</strong><p>{gate.decisionNote}</p></div>}

      {approvals.length > 0 && <details className="approval-history">
        <summary>Decision history · {approvals.length}</summary>
        <div>
          {approvals.map(approval => <article key={approval.id} className={`is-${approval.status}`}>
            <span>{approval.status === 'approved' ? <CheckCircleFilled /> : approval.status === 'rejected' ? <CloseCircleFilled /> : <ClockCircleFilled />}</span>
            <p><strong>{approval.status === 'approved' ? 'Approved' : approval.status === 'rejected' ? 'Rejected' : 'Pending decision'}</strong><small><code>{approval.id}</code> · requested by {approval.requestedBy}</small>{approval.decisionNote && <em>{approval.decisionNote}</em>}</p>
            <time>{new Date(approval.decidedAt ?? approval.requestedAt).toLocaleString()}</time>
          </article>)}
        </div>
      </details>}

      {gate?.status === 'pending' && <div className="approval-decision">
        <div><TeamOutlined /><span><strong>Demo role switch · Anna Svensson</strong><small>Preset Release Manager persona · separate from requester Bohua Li</small></span></div>
        <Input.TextArea aria-label="Approval decision note" rows={2} maxLength={400} value={note} onChange={event => setNote(event.target.value)} />
        <div><Button danger loading={busy} onClick={() => void decide('rejected')}>Reject</Button><Button type="primary" loading={busy} disabled={note.trim().length < 8} onClick={() => void decide('approved')}>Approve exact scope</Button></div>
      </div>}

      {artifact && qualityReady && gate?.status !== 'pending' && gate?.status !== 'approved' && <div className="approval-actions">
        <span><TeamOutlined /> Requested by Bohua Li · Release Owner</span>
        <Button type="primary" loading={busy} onClick={() => void request()}>{gate?.status === 'rejected' ? 'Request approval again' : 'Request release approval'}</Button>
      </div>}
    </section>
  );
};
